import { Form as BaseForm } from "@base-ui-components/react/form";
import { Formik, FormikConfig, FormikProps } from "formik";
import { forwardRef } from "react";
import React from "react";

type Errors = Record<string, string | string[]>;

interface FormProps<Values extends Record<string, any>> {
  // Initial values for the form
  initialValues: Values;

  // Optional validation schema (e.g., Yup schema)
  validationSchema?: FormikConfig<Values>["validationSchema"];

  // Function to handle form submission
  onSubmit: (values: Values) => void | Promise<void>;

  // Render prop for children - receives formik instance
  children:
    | ((formik: FormikProps<Values>) => React.ReactNode)
    | React.ReactNode;

  // Enables form validation on change, blur, or submit
  validateOn?: "change" | "blur" | "submit";
  encType?: string;

  // Optional className for styling
  className?: string;
}

function InnerForm<Values extends Record<string, any>>(
  props: FormProps<Values>,
  ref: React.Ref<FormikProps<Values>>
) {
  const {
    initialValues,
    validationSchema,
    onSubmit,
    children,
    validateOn = "submit",
    className,
    ...rest
  } = props;
  // Configure validation triggers based on validateOn prop
  const validateOnChange = validateOn === "change";
  const validateOnBlur = validateOn === "blur";

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnChange={validateOnChange}
      validateOnBlur={validateOnBlur}
      validateOnMount={false}
      innerRef={ref}
    >
      {(formik) => (
        <BaseForm
          onSubmit={formik.handleSubmit}
          className={className}
          errors={formik.errors as Errors}
          //@ts-ignore
          onClearErrors={formik.setErrors}
          {...rest}
        >
          {typeof children === "function" ? children(formik) : children}
        </BaseForm>
      )}
    </Formik>
  );
}

const Form = forwardRef(InnerForm) as <Values extends Record<string, any>>(
  props: FormProps<Values> & { ref?: React.Ref<FormikProps<Values>> }
) => React.ReactElement;

export default Form;
